import type { CCPreset } from '~/types/presets';
import type { MidiCC } from '~/types/midi';
import { getYoTestSynthDefaultProfile } from '~/lib/profiles/yoTestSynthProfile';

/**
 * Default CC preset for YO! Monosynth
 *
 * This preset provides a carefully tuned set of MIDI CC parameters
 * optimized for subtractive synthesis. Based on Moog Subsequent 37-style
 * CC mapping documented in docs/SYNTH_CC_MAPPING.md
 *
 * Key characteristics:
 * - Filter-focused sound design with cutoff and resonance control
 * - Dual envelope control (amp + filter) for expressive articulation
 * - Subtractive synthesis parameters for warm, analog-style sounds
 */

const defaultMonosynthCCs: MidiCC[] = [
  { number: 1, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 7, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 11, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 16, value: 8, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 17, value: 14, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 18, value: 58, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 19, value: 96, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 20, value: 31, scramble: true, smooth: false, show: true, min: 0, max: 70 },
  { number: 21, value: 68, scramble: true, smooth: false, show: true, min: 20, max: 100 },
  { number: 70, value: 33, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 71, value: 76, scramble: true, smooth: false, show: true, min: 0, max: 80 },
  { number: 72, value: 97, scramble: true, smooth: false, show: true, min: 10, max: 127 },
  { number: 73, value: 3, scramble: true, smooth: false, show: true, min: 3, max: 80 },
  { number: 74, value: 81, scramble: true, smooth: false, show: true, min: 50, max: 110 },
  { number: 75, value: 93, scramble: true, smooth: false, show: true, min: 10, max: 127 },
  { number: 76, value: 14, scramble: true, smooth: false, show: true, min: 0, max: 127 },
];

/**
 * Default Monosynth CC Preset
 */
export const DEFAULT_MONOSYNTH_PRESET: CCPreset = {
  id: 'default-mono',
  name: 'Default Monosynth',
  deviceType: 'MonoSynth',
  cc: defaultMonosynthCCs,
  profiles: [getYoTestSynthDefaultProfile()],
  activeProfileId: 'yo-test-synth-default',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the default Monosynth CC preset
 * Returns a deep copy to avoid mutation
 */
export function getDefaultMonosynthPreset(): CCPreset {
  return {
    ...DEFAULT_MONOSYNTH_PRESET,
    cc: DEFAULT_MONOSYNTH_PRESET.cc.map(cc => ({ ...cc })),
    profiles: DEFAULT_MONOSYNTH_PRESET.profiles.map(profile => ({
      ...profile,
      ccConfig: profile.ccConfig.map(config => ({ ...config })),
    })),
  };
}
